import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import { TaxProviderKind, type Store } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@nucleus-commerce/ui/components/form';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useUpdateStore } from '@/features/stores';
import { SECTION_ANCHORS } from '@/shared/navigation/section-anchors';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { fromTaxSettings, toTaxSettingsPatch } from '../api/mappers';
import { taxSettingsSchema, type TaxSettingsForm } from '../schemas/tax-rate-form-schema';

const mapField = (field: string) => field.replace(/^settings\.tax\./, '');
const KNOWN_FIELDS: ReadonlySet<string> = new Set(['provider', 'pricesIncludeTax']);

/** `settings.tax` of the store, saved through the store PATCH which merges the group. */
export function TaxSettingsCard({ store }: { store: Store }) {
  const { t } = useTranslation(['tax', 'common']);
  const readOnly = !useStoreEditable({ store, permission: 'settings.write' }).editable;
  const update = useUpdateStore(store.id, 'tax:toasts.settingsSaved');
  const message = useErrorMessage();
  const form = useForm<TaxSettingsForm>({
    resolver: zodResolver(taxSettingsSchema),
    defaultValues: fromTaxSettings(store),
  });
  const provider = form.watch('provider');

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await update.mutateAsync(toTaxSettingsPatch(values));
      form.reset(values);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });
  useSaveBar({ form, onSave: submit, saving: update.isPending, disabled: readOnly });

  return (
    <FormSection
      title={t('tax:settings.title')}
      description={t('tax:settings.description')}
      id={SECTION_ANCHORS.taxCalculation}
      tour="settings.tax.settings"
    >
      <Form {...form}>
        <form onSubmit={submit} noValidate className="flex flex-col gap-4">
          {form.formState.errors.root?.server?.message ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
            </Alert>
          ) : null}
          <FormField
            control={form.control}
            name="provider"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('tax:settings.provider')}</FormLabel>
                <FormControl>
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={readOnly}
                    className="grid gap-2 sm:grid-cols-2"
                  >
                    {Object.values(TaxProviderKind).map((kind) => (
                      <label
                        key={kind}
                        htmlFor={`tax-provider-${kind}`}
                        className={cn(
                          'flex cursor-pointer items-start gap-3 rounded-md border p-3',
                          provider === kind && 'border-primary bg-primary-tint/40',
                        )}
                      >
                        <RadioGroupItem
                          id={`tax-provider-${kind}`}
                          value={kind}
                          className="mt-0.5"
                        />
                        <span className="grid gap-0.5 text-sm">
                          <span className="font-medium">{t(`tax:provider.${kind}`)}</span>
                          <span className="text-muted-foreground text-xs">
                            {t(`tax:providerHint.${kind}`)}
                          </span>
                        </span>
                      </label>
                    ))}
                  </RadioGroup>
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="pricesIncludeTax"
            render={({ field }) => (
              <FormItem
                className="flex flex-row items-center justify-between gap-4 rounded-md border p-3"
                data-tour="settings.tax.prices-include-tax"
              >
                <div className="grid gap-1">
                  <FormLabel>{t('tax:settings.pricesIncludeTax')}</FormLabel>
                  <FormDescription>{t('tax:settings.pricesIncludeTaxHint')}</FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={readOnly}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          {readOnly ? null : (
            <div>
              <Button type="submit" disabled={update.isPending || !form.formState.isDirty}>
                {update.isPending ? t('common:actions.saving') : t('tax:settings.submit')}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </FormSection>
  );
}
